import React, { FunctionComponent } from "react";
import styled from "styled-components";
import { RowElementContainer } from "features/ActivityList/style";
import { Tooltip, Text } from "@vygruppen/spor-react";
import { ReactComponent as BusWashSVG } from "features/ActivityList/icons/VaskBuss.svg";
import { ReactComponent as WashSVG } from "features/ActivityList/icons/Vask.svg";
import { ReactComponent as TankSVG } from "features/ActivityList/icons/Tank.svg";
import { ReactComponent as CoolantSVG } from "features/ActivityList/icons/Coolant.svg";
import { ReactComponent as WasherFluidSVG } from "features/ActivityList/icons/WasherFluid.svg";
import { ReactComponent as OilSVG } from "features/ActivityList/icons/Oil.svg";
import { ReactComponent as SafetyCheckSVG } from "features/ActivityList/icons/SafetyCheck.svg";
import { ReactComponent as AdBlueSVG } from "features/ActivityList/icons/AdBlue.svg";
import { ReactComponent as RundvaskSVG } from "features/ActivityList/icons/Rundvask.svg";
import { ReactComponent as ToiletSVG } from "features/ActivityList/icons/Toilet.svg";
import { ReactComponent as DownSVG } from "features/ActivityList/icons/Down.svg";
import { ReactComponent as UpSVG } from "features/ActivityList/icons/Up.svg";
import { ServiceLocation } from "../../api/locations";
import { safetyCheckEnabledForLocation } from "./LocationConfiguration";
import { getScreenSize, ScreenSize, useWindowDimensions } from "App";
import { getColumnSize } from "./Size";

const BusWashIcon = styled(BusWashSVG)`
  width: 30px;
`;

const WashIcon = styled(WashSVG)`
  width: 30px;
`;

const TankIcon = styled(TankSVG)`
  width: 30px;
`;

const CoolantIcon = styled(CoolantSVG)`
  width: 30px;
`;

const WasherFluidIcon = styled(WasherFluidSVG)`
  width: 30px;
`;

const OilIcon = styled(OilSVG)`
  width: 30px;
`;

const SafetyCheckIcon = styled(SafetyCheckSVG)`
  width: 30px;
`;

const AdBlueIcon = styled(AdBlueSVG)`
  width: 30px;
`;

const RundVaskIcon = styled(RundvaskSVG)`
  width: 30px;
`;

const SortButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  margin-left: 8px;
  padding: 2px;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-radius: 4px;
  }
`;

const ArrowIcon = styled.div<{ highlighted: boolean }>`
  opacity: ${(props) => (props.highlighted ? 1 : 0.3)};
  transition: opacity 0.2s;
  line-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
    transform: scale(1.3);
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 95px;
  background-color: #b9c9de;
  padding-left: 24px;
  margin-top: 20px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  box-sizing: border-box;
  z-index: 1;
`;

interface IHeaderProps {
  selectedLocation: ServiceLocation;
  containsRundvask: boolean;
  sortByStartTimeNextBlock?: "off" | "asc" | "desc";
  onSortByStartTimeNextBlock?: () => void;
}

export const Header: FunctionComponent<IHeaderProps> = ({
  selectedLocation,
  containsRundvask,
  sortByStartTimeNextBlock = "off",
  onSortByStartTimeNextBlock,
}) => {
  const { width } = useWindowDimensions();
  const screenSize = getScreenSize(width);
  const columnSize = getColumnSize(screenSize);

  const label = (no: string, se: string) =>
    selectedLocation.countryCode === "SE" ? se : no;

  const isWide = screenSize > ScreenSize.NARROW;

  const IconCell = ({
    ariaLabel,
    wideText,
    Icon,
  }: {
    ariaLabel: string;
    wideText: React.ReactNode;
    Icon: React.ReactNode;
  }) => (
    <RowElementContainer
      minWidth={columnSize.minWidth}
      width={columnSize.width}
    >
      {isWide ? (
        <>
          <Text align="center">{wideText}</Text>
          {Icon}
        </>
      ) : (
        <Tooltip content={ariaLabel}>
          <span aria-label={ariaLabel}>{Icon}</span>
        </Tooltip>
      )}
    </RowElementContainer>
  );

  return (
    <HeaderContainer>
      <RowElementContainer
        minWidth={screenSize < ScreenSize.LARGE ? "70px" : "125px"}
        width={screenSize < ScreenSize.LARGE ? "70px" : "null"}
      >
        <Text>Tid</Text>
      </RowElementContainer>

      <RowElementContainer
        minWidth={screenSize < ScreenSize.LARGE ? "70px" : "125px"}
        width={screenSize < ScreenSize.LARGE ? "70px" : "null"}
        style={{
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
        }}
      >
        <Text style={{ flex: 1 }}>
          {selectedLocation.countryCode === "SE" ? (
            <>
              Starttid
              <br />
              nästa block
            </>
          ) : (
            <>
              Starttid
              <br />
              neste blokk
            </>
          )}
        </Text>
        <SortButtonContainer onClick={onSortByStartTimeNextBlock}>
          <ArrowIcon highlighted={sortByStartTimeNextBlock === "asc"}>
            <UpSVG />
          </ArrowIcon>
          <ArrowIcon highlighted={sortByStartTimeNextBlock === "desc"}>
            <DownSVG />
          </ArrowIcon>
        </SortButtonContainer>
      </RowElementContainer>

      <RowElementContainer
        minWidth={screenSize <= ScreenSize.NARROW ? "90px" : "140px"}
        width={screenSize <= ScreenSize.NARROW ? "90px" : "140px"}
      >
        <Text>Buss</Text>
      </RowElementContainer>

      <IconCell
        ariaLabel={label("Tanking", "Tankning")}
        wideText={
          <>
            <br />
            {label("Tanking", "Tankning")}
          </>
        }
        Icon={<TankIcon />}
      />

      <IconCell
        ariaLabel={label("Vask inne", "Städ av buss")}
        wideText={
          selectedLocation.countryCode === "SE" ? (
            <>
              Städ
              <br />
              av buss
            </>
          ) : (
            <>
              Innvendig
              <br />
              vask
            </>
          )
        }
        Icon={<WashIcon />}
      />

      <IconCell
        ariaLabel={label("Vask ute", "Tvätt av buss")}
        wideText={
          selectedLocation.countryCode === "SE" ? (
            <>
              Tvätt
              <br />
              av buss
            </>
          ) : (
            <>
              Utvendig
              <br />
              vask
            </>
          )
        }
        Icon={<BusWashIcon />}
      />

      <IconCell
        ariaLabel={label("Kjølevæske", "Kylvätska")}
        wideText={
          selectedLocation.countryCode === "SE" ? (
            <>
              Kyl-
              <br />
              vätska
            </>
          ) : (
            <>
              Kjøle-
              <br />
              væske
            </>
          )
        }
        Icon={<CoolantIcon />}
      />

      <IconCell
        ariaLabel={label("Spylervæske", "Spolarvätska")}
        wideText={
          selectedLocation.countryCode === "SE" ? (
            <>
              Spolar-
              <br />
              vätska
            </>
          ) : (
            <>
              Spyler-
              <br />
              væske
            </>
          )
        }
        Icon={<WasherFluidIcon />}
      />

      <IconCell
        ariaLabel={label("Olje", "Olja")}
        wideText={
          <>
            <br />
            {label("Olje", "Olja")}
          </>
        }
        Icon={<OilIcon />}
      />

      <IconCell
        ariaLabel="AdBlue"
        wideText={
          <>
            <br />
            AdBlue
          </>
        }
        Icon={<AdBlueIcon />}
      />

      <IconCell
        ariaLabel="Toalett"
        wideText={
          selectedLocation.countryCode === "SE" ? (
            <>
              Tömma
              <br />
              toalett
            </>
          ) : (
            <>
              Tømming
              <br />
              toalett
            </>
          )
        }
        Icon={<ToiletSVG />}
      />

      {safetyCheckEnabledForLocation(selectedLocation) && (
        <IconCell
          ariaLabel={label("Sikkerhetskontroll", "Säkerhetskontroll")}
          wideText={
            selectedLocation.countryCode === "SE" ? (
              <>
                Säkerhets-
                <br />
                kontroll
              </>
            ) : (
              <>
                Sikkerhets-
                <br />
                kontroll
              </>
            )
          }
          Icon={<SafetyCheckIcon />}
        />
      )}

      {containsRundvask && (
        <IconCell
          ariaLabel="Rundvask"
          wideText={
            <>
              <br />
              Rundvask
            </>
          }
          Icon={<RundVaskIcon />}
        />
      )}
    </HeaderContainer>
  );
};
